import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRobot, faTimes } from '@fortawesome/free-solid-svg-icons';

const AIModal = ({ isOpen, onClose }) => {
  const [aiText, setAiText] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);

  useEffect(() => {
    if (isOpen) {
      handleAIAnalysis();
    }
  }, [isOpen]);

  const handleAIAnalysis = async () => {
    setIsStreaming(true);
    setAiText('🔍 Analyzing system metrics...\n\nThis will take a few seconds. Please wait.');

    try {
      const API_BASE_URL = (window.ENV && window.ENV.API_URL) || 'https://spheroid.scaleapp.tech/api';
      const response = await fetch(`${API_BASE_URL}/ai-analysis`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          metrics: {
            activePipelines: 127,
            systemLoad: 67.8,
            dataTransfer: 234.56,
            processingSpeed: 1.24,
            activeUsers: 12,
            problems: 7,
            dockerContainers: 122,
            networkVolume: 131
          }
        })
      });

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      let buffer = '';
      let isFirstChunk = true;

      const displayWordByWord = async (text) => {
        const words = text.split(/(\s+)/);
        for (const word of words) {
          setAiText(prev => prev + word);
          await new Promise(resolve => setTimeout(resolve, 30));
        }
      };

      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          if (buffer) {
            await displayWordByWord(buffer);
          }
          break;
        }

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              if (data.text) {
                if (isFirstChunk) {
                  setAiText('');
                  isFirstChunk = false;
                }
                buffer += data.text;
                if (buffer.length > 50 || buffer.includes('.') || buffer.includes('\n')) {
                  await displayWordByWord(buffer);
                  buffer = '';
                }
              }
              if (data.done) {
                if (buffer) {
                  await displayWordByWord(buffer);
                }
                setIsStreaming(false);
              }
              if (data.error) {
                setAiText(prev => prev + `\n\n❌ Error: ${data.error}`);
                setIsStreaming(false);
              }
            } catch (e) {
              console.error('Parse error:', e);
            }
          }
        }
      }
    } catch (error) {
      console.error('AI Analysis error:', error);
      setAiText(`❌ Failed to connect to AI analysis service.\n\nPlease check the backend connection.`);
      setIsStreaming(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="ai-modal-overlay" onClick={onClose}>
      <div className="ai-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="ai-modal-header">
          <h3>
            <FontAwesomeIcon icon={faRobot} style={{ marginRight: '0.5rem', color: '#3b82f6' }} />
            AI System Analysis
          </h3>
          <button className="ai-modal-close" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        <div className="ai-modal-body">
          <div className="ai-text-content">
            {aiText.split('\n').map((line, index) => {
              let formattedLine = line;
              formattedLine = formattedLine.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

              const isHeader = line.match(/^(#{1,3})\s+(.+)$/);
              if (isHeader) {
                const level = isHeader[1].length;
                const text = isHeader[2];
                return (
                  <h4 key={index} style={{
                    fontSize: level === 1 ? '1.1rem' : level === 2 ? '1rem' : '0.95rem',
                    fontWeight: 'bold',
                    marginTop: level === 1 ? '1rem' : '0.8rem',
                    marginBottom: '0.4rem',
                    color: '#3b82f6'
                  }}>
                    {text}
                  </h4>
                );
              }

              return (
                <p key={index} dangerouslySetInnerHTML={{ __html: formattedLine }} />
              );
            })}
            {isStreaming && <span className="ai-cursor">▊</span>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIModal;
